/** Type guards for Ory flows, nodes, and responses. */

import type { OryKratosFlowWithUI } from '../model';
import type { 
  UiNode, 
  UiNodeInputAttributes,
  UiNodeTextAttributes,
  UiNodeImageAttributes,
  UiNodeScriptAttributes,
  UiNodeAnchorAttributes,
} from '@ory/client';

// ============================================================================
// Flow Guards
// ============================================================================

/** Checks if value is a flow with UI components. Narrows to `OryKratosFlowWithUI`. */
export const isOryFlowWithUI = (value: unknown): value is OryKratosFlowWithUI => {
  return typeof value === 'object' && value !== null && 'ui' in value;
};

// ============================================================================
// Flow Property Guards
// ============================================================================

/**
 * Type guard to check if a config object has returnTo property.
 */
export const isOryConfigWithReturn = (value: unknown): value is { returnTo: string } => {
  return typeof value === 'object' && value !== null && 'returnTo' in value;
};

/**
 * Type guard to check if a flow has return_to property.
 */
export const isOryFlowWithReturnTo = (value: unknown): value is { return_to: string } => {
  return typeof value === 'object' && value !== null && 'return_to' in value;
};

// ============================================================================
// UI Node Guards
// ============================================================================

// Note: Response guards (isOryResponseWithSession, isOryResponseWithContinueWith, isOAuth2RedirectTo)
// have been moved to response-helpers.ts for better cohesion.
// All response-related logic (guards + data extraction) is now in one place.

export const isOryInputNode = (node: UiNode): node is UiNode & { attributes: UiNodeInputAttributes } => {
  return node.type === 'input' && 'name' in node.attributes;
};

export const isOryCsrfTokenNode = (node: UiNode): node is UiNode & { attributes: UiNodeInputAttributes } => {
  return isOryInputNode(node) && node.attributes.name === 'csrf_token';
};

export const isOryTextNode = (node: UiNode): node is UiNode & { attributes: UiNodeTextAttributes } => {
  return node.type === 'text' && 'text' in node.attributes;
};

export const isOryImageNode = (node: UiNode): node is UiNode & { attributes: UiNodeImageAttributes } => {
  return node.type === 'img' && 'src' in node.attributes;
};

export const isOryScriptNode = (node: UiNode): node is UiNode & { attributes: UiNodeScriptAttributes } => {
  return node.type === 'script' && 'src' in node.attributes;
};

export const isOryAnchorNode = (node: UiNode): node is UiNode & { attributes: UiNodeAnchorAttributes } => {
  return node.type === 'a' && 'href' in node.attributes;
};
